import Label from '@/components/inputs/Label';
import Select from '@/components/inputs/Select';
import DatePickerInput from '@/components/modal/HistoricSearchModal/DatePickerInput';
import { ExtendedDatePicker, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { TableFilter } from '@/types/table';
import { getDateFormat } from '@/utils/format';
import useLanguage from '@/utils/hooks/useLanguage';
import { useLocale } from '@react-aria/i18n';
import {
    addDays,
    differenceInDays,
    endOfMonth,
    endOfWeek,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
    subWeeks,
    subYears
} from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { default as DatePicker } from 'react-datepicker';

type Period = 'custom' | 'daily' | 'weekToDate' | 'lastWeek' | 'monthToDate' | 'lastMonth';
export type PeriodOption = { label: string; value: Period };

const periodOptions: PeriodOption[] = [
    { label: 'Custom', value: 'custom' },
    { label: 'Daily', value: 'daily' },
    { label: 'Week to Date', value: 'weekToDate' },
    { label: 'Last Week', value: 'lastWeek' },
    { label: 'Month to Date', value: 'monthToDate' },
    { label: 'Last Month', value: 'lastMonth' }
];

interface DateRangeFiltersProps {
    startDate: TableFilter<Date>;
    endDate: TableFilter<Date>;
    setFilterFields: (names: string[], values: TableFilter<Date>[]) => void;
    allowYearRange?: boolean;
}

const DateRangeFilters = (props: DateRangeFiltersProps) => {
    const { startDate, endDate, setFilterFields, allowYearRange = false } = props;
    const currentTradeDate = getOperationalCurrentTradeDate();

    const lang = useLanguage();
    const { locale } = useLocale();
    const dateFormatPattern = getDateFormat(locale);

    const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(periodOptions[0]);

    const startRef = useRef<ExtendedDatePicker | null>(null);
    const endRef = useRef<ExtendedDatePicker | null>(null);

    const defaultFromDatePickerProps = useMemo(() => {
        return {
            locale: locale.includes('en') ? 'en' : locale,
            minDate: subYears(endDate.value, 1),
            maxDate: selectedPeriod.value === 'daily' ? currentTradeDate : endDate.value
        };
    }, [locale, endDate, selectedPeriod.value]);

    const defaultToDatePickerProps = useMemo(() => {
        return {
            locale: locale.includes('en') ? 'en' : locale,
            minDate: startDate.value,
            maxDate: currentTradeDate
        };
    }, [locale, startDate]);

    const disabledDateInputs = useMemo(() => {
        if (selectedPeriod.value === 'custom') return { startDate: false, endDate: false };
        else if (selectedPeriod.value === 'daily') return { startDate: false, endDate: true };
        else return { startDate: true, endDate: true };
    }, [selectedPeriod]);

    useEffect(() => {
        if (selectedPeriod.value === 'daily') {
            setFilterFields(
                ['endDate'],
                [
                    {
                        value: startDate.value,
                        label: lang.common.endDate
                    }
                ]
            );
        }
    }, [selectedPeriod, startDate]);

    useEffect(() => {
        if (selectedPeriod.value === 'weekToDate') {
            setFilterFields(
                ['startDate', 'endDate'],
                [
                    {
                        value: startOfWeek(currentTradeDate, { weekStartsOn: 1 }),
                        label: lang.common.startDate
                    },
                    {
                        value: currentTradeDate,
                        label: lang.common.endDate
                    }
                ]
            );
        }
        if (selectedPeriod.value === 'lastWeek') {
            setFilterFields(
                ['startDate', 'endDate'],
                [
                    {
                        value: subWeeks(startOfWeek(currentTradeDate, { weekStartsOn: 1 }), 1),
                        label: lang.common.startDate
                    },
                    {
                        value: subWeeks(endOfWeek(currentTradeDate, { weekStartsOn: 1 }), 1),
                        label: lang.common.endDate
                    }
                ]
            );
        }
        if (selectedPeriod.value === 'monthToDate') {
            setFilterFields(
                ['startDate', 'endDate'],
                [
                    {
                        value: startOfMonth(currentTradeDate),
                        label: lang.common.startDate
                    },
                    {
                        value: currentTradeDate,
                        label: lang.common.endDate
                    }
                ]
            );
        }
        if (selectedPeriod.value === 'lastMonth') {
            setFilterFields(
                ['startDate', 'endDate'],
                [
                    {
                        value: startOfMonth(subMonths(currentTradeDate, 1)),
                        label: lang.common.startDate
                    },
                    {
                        value: endOfMonth(subMonths(currentTradeDate, 1)),
                        label: lang.common.endDate
                    }
                ]
            );
        }
    }, [selectedPeriod]);

    return (
        <div className="flex items-center gap-2 h-[34px] text-xs w-fit">
            <Label className="sm:min-w-32" label={lang.common.dateRange} />
            <div className="flex items-center gap-3 flex-row">
                <div>
                    <Select
                        // label="Period"
                        options={periodOptions}
                        selected={selectedPeriod}
                        className="!text-xs"
                        iconClassName="!w-4 !h-4"
                        onChange={(value) => setSelectedPeriod(value)}
                    />
                </div>
                <span>
                    <DatePicker
                        ref={startRef}
                        selected={startDate.value}
                        dateFormat={dateFormatPattern}
                        onChange={(date) => {
                            // Convert selected local date to date in 00:00:00
                            const simpleDate = new Date(`${date?.toDateString()} 00:00:00`);

                            if (!allowYearRange) {
                                if (differenceInDays(endDate.value, simpleDate) > 31) {
                                    setFilterFields(
                                        ['startDate', 'endDate'],
                                        [
                                            {
                                                value: simpleDate,
                                                label: lang.common.startDate
                                            },
                                            {
                                                value: addDays(simpleDate, 31),
                                                label: lang.common.endDate
                                            }
                                        ]
                                    );
                                } else {
                                    setFilterFields(
                                        ['startDate'],
                                        [
                                            {
                                                value: simpleDate,
                                                label: lang.common.startDate
                                            }
                                        ]
                                    );
                                }
                            } else {
                                setFilterFields(
                                    ['startDate'],
                                    [
                                        {
                                            value: simpleDate,
                                            label: lang.common.startDate
                                        }
                                    ]
                                );
                            }
                            startRef.current?.setOpen(false);
                        }}
                        customInput={
                            <DatePickerInput
                                parentRef={startRef}
                                label={lang.common.startDate}
                                labelClassName="!text-2xs"
                                className="h-[34px] pl-3 !text-xs"
                                inline
                            />
                        }
                        disabled={disabledDateInputs.startDate}
                        disabledKeyboardNavigation
                        {...defaultFromDatePickerProps}
                    />
                </span>
                <span>-</span>
                <span>
                    <DatePicker
                        ref={endRef}
                        selected={endDate.value}
                        dateFormat={dateFormatPattern}
                        onChange={(date) => {
                            // Convert selected local date to date in 00:00:00
                            const simpleDate = new Date(`${date?.toDateString()} 00:00:00`);
                            if (!allowYearRange) {
                                if (differenceInDays(simpleDate, startDate.value) > 31) {
                                    setFilterFields(
                                        ['startDate', 'endDate'],
                                        [
                                            {
                                                value: subDays(simpleDate, 31),
                                                label: lang.common.startDate
                                            },
                                            {
                                                value: simpleDate,
                                                label: lang.common.endDate
                                            }
                                        ]
                                    );
                                } else {
                                    setFilterFields(
                                        ['endDate'],
                                        [
                                            {
                                                value: simpleDate,
                                                label: lang.common.endDate
                                            }
                                        ]
                                    );
                                }
                            } else {
                                setFilterFields(
                                    ['endDate'],
                                    [
                                        {
                                            value: simpleDate,
                                            label: lang.common.endDate
                                        }
                                    ]
                                );
                            }

                            endRef.current?.setOpen(false);
                        }}
                        customInput={
                            <DatePickerInput
                                parentRef={endRef}
                                label={lang.common.endDate}
                                labelClassName="!text-2xs"
                                className="h-[34px] pl-3 !text-xs"
                                inline
                            />
                        }
                        disabled={disabledDateInputs.endDate}
                        disabledKeyboardNavigation
                        {...defaultToDatePickerProps}
                    />
                </span>
            </div>
        </div>
    );
};

export default DateRangeFilters;
